import { z } from 'zod';
import {
  itineraryProcessStepSchema,
  itineraryProcessTargetsSchema,
} from './itinerary-search';

/**
 * XT-708 — the declarative itinerary status process.
 *
 * Shaped by what was decided:
 *
 *  1. **A status's position in the process IS its sales stage.** There is no
 *     separate step index: moving a status between stages is the move.
 *  2. **A step may target one status, any status, or any status in a stage.**
 *     A wildcard states "from here you may go anywhere" without enumerating
 *     every status, and stays correct when a new status is added later.
 *  3. **Stages are fixed.** They come from `itinerary_status.sales_stage`;
 *     this feature assigns statuses to them and never invents them.
 *  4. **Stage 0 means "not in the process"**, and a status there may lead only
 *     to a Stage 1 status — those statuses still hold real itineraries, so they
 *     need exactly one way back in.
 *
 * Actions on entering / leaving a status are READ-ONLY and **derived at read
 * time** from the code that performs them — the sale-date stamp, the item
 * initialisation, the cancellation cascade. They are not stored, so they cannot
 * drift from the behaviour they describe. Configuring them is a later phase,
 * and that is when storage will be needed.
 */

/** Stage 0 doubles as "not in the process" (decision 4). */
export const UNPLACED_SALES_STAGE = 0;
/** The only destination a Stage 0 status may have (decision 4). */
export const REENTRY_SALES_STAGE = 1;

/**
 * The sales stages, in one place. Decision 3 says this feature assigns statuses
 * to stages and never invents them, so the set is fixed — and `save` rejects
 * anything outside it rather than quietly writing a stage no screen can show.
 *
 * Prod carried exactly five (verified 2026-09-07: 0, 1, 4, 10, 20); XT-816
 * adds 3, Price Review — a trip the agent portal sent in whose prices the
 * consultant has yet to check (the traveller sees it without prices).
 * Comments elsewhere in the repo claim stages 7 and 9 are also in use; they are
 * not, and `api-shared/itinerary-sale-dates` already records that correction.
 * The editor still renders a column for any other stage it finds, so a status
 * can never become invisible — see `salesStageColumns`.
 */
export const SALES_STAGES: { value: number; label: string }[] = [
  { value: 0, label: 'Not in the process' },
  { value: 1, label: 'Stage 1 — Draft' },
  { value: 3, label: 'Stage 3 — Price Review' },
  { value: 4, label: 'Stage 4 — Proposal' },
  { value: 10, label: 'Stage 10 — Sold' },
  { value: 20, label: 'Stage 20 — Cancelled' },
];

export const isKnownSalesStage = (salesStage: number): boolean =>
  SALES_STAGES.some((s) => s.value === salesStage);

export const salesStageLabel = (salesStage: number): string =>
  SALES_STAGES.find((s) => s.value === salesStage)?.label ??
  `Stage ${salesStage}`;

/**
 * Columns to render: the canonical stages, plus any other stage the data
 * actually contains. Deriving the extras is what stops a status at an
 * unexpected stage from vanishing from the board — and, worse, being swept to
 * stage 0 by "Clear" while invisible.
 */
export const salesStageColumns = (
  stagesInUse: readonly (number | null)[]
): { value: number; label: string }[] => {
  const extras = [
    ...new Set(
      stagesInUse.filter((s): s is number => s != null && !isKnownSalesStage(s))
    ),
  ].sort((a, b) => a - b);
  return [
    ...SALES_STAGES,
    ...extras.map((value) => ({ value, label: `Stage ${value}` })),
  ];
};

// --- Actions -------------------------------------------------------------

/**
 * Every side effect a status change can have, named once. Each type is
 * implemented ONCE against a common interface and reads its org-specific
 * settings from `config` — which is what lets the eight-org allow-list in
 * `itineraries/service.ts` become rows rather than source.
 */
export const statusActionTypeValues = [
  // Stamps `sold_on` / `cancelled_on`. Destination-only and write-once — see
  // `@travel-planner/api-shared/itinerary-sale-dates`.
  'stamp_sale_date',
  // Initializes items that carry NO operation status yet. Never rewrites one.
  'init_item_operation_status',
  // Forces every item onto an operation status (the cancellation cascade).
  'set_item_operation_status',
  // Instantiates a task collection (today: `task_collection.status_config`).
  'assign_task_collection',
  // Traveller-preview visibility (today: the dead `preview_trigger` column and
  // the customer-preview lifecycle's `disabled` writes).
  'publish_preview',
  'unpublish_preview',
  'send_email',
  'create_notification',
  // --- Operation flow ---------------------------------------------------
  // One library serves both status flows, so these live in the same list.
  // Which of them an action may be attached to is decided by the catalog's
  // `scope`, not by the type name.
  //
  // Forces every active sub-service of a main service onto the same status —
  // today's S0 "Modified" cascade.
  'cascade_status_to_subitems',
  // Drops the service's failed WhatsApp sends.
  'clear_failed_messages',
  // Moves the itinerary on once every one of its services is complete.
  'finalize_itinerary_when_complete',
  // Moves the MAIN service up once every one of its sub-services has arrived
  // at this status — the upward counterpart of the cascade above.
  'roll_up_status_to_parent',
  // Moves the service on once its end time has passed (the only time-driven
  // action in the library; a cron sweep performs it, not a status change).
  'advance_when_end_time_passed',
] as const;
export const statusActionTypeSchema = z.enum(statusActionTypeValues);

/**
 * Which status flow a thing belongs to.
 *
 * The two flows share one action library and one action log, so `scope` is
 * what keeps them apart: it decides which actions a status may be given, and
 * which rows the log's flow filter shows.
 */
export const statusFlowScopeValues = ['itinerary', 'operation'] as const;
export const statusFlowScopeSchema = z.enum(statusFlowScopeValues);
export type StatusFlowScope = z.infer<typeof statusFlowScopeSchema>;
export type StatusActionType = z.infer<typeof statusActionTypeSchema>;

/**
 * `config` is intentionally open: each action type owns its own shape, and
 * narrowing happens in the executor that implements it. Keeping it loose here
 * means a new action type does not require a wire-schema change in lockstep.
 */
export const statusActionSchema = z.object({
  type: statusActionTypeSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  // Free-text, rendered on screen. The point of the whole ticket is that a
  // non-developer can read what a status does.
  description: z.string().nullable().default(null),
  /**
   * True when this describes behaviour the application ALREADY performs, worked
   * out from the code at read time rather than stored.
   *
   * Derived rather than seeded on purpose: a stored copy of what the code does
   * can drift from it silently, which is exactly what happened to
   * `itinerary_status.preview_trigger` — a seeded column no code reads. A
   * derived answer cannot go stale, and needs no test to police it. Derived
   * entries are read-only and are never written back on save.
   */
  isDerived: z.boolean().default(false),
});
export type StatusAction = z.infer<typeof statusActionSchema>;

export const statusActionListSchema = z.array(statusActionSchema);

// --- Guards --------------------------------------------------------------

/**
 * A guard is BOTH the precondition and the UI that satisfies it — which is
 * where "what dialog is shown when" lives. Today's two hardcoded dialogs are
 * `confirm` (Sold) and `collect_field` (the cancellation reason).
 */
export const transitionGuardTypeValues = [
  // Blocks until a field on the itinerary is set, e.g. `contact_id`.
  'require_field',
  // Confirmation dialog; no data captured.
  'confirm',
  // Dialog captures a value and persists it to `config.field`.
  'collect_field',
  // The actor must hold a permission to take this transition.
  'require_permission',
] as const;
export const transitionGuardTypeSchema = z.enum(transitionGuardTypeValues);
export type TransitionGuardType = z.infer<typeof transitionGuardTypeSchema>;

export const transitionGuardSchema = z.object({
  type: transitionGuardTypeSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  description: z.string().nullable().default(null),
  /** See `statusActionSchema.isDerived`. */
  isDerived: z.boolean().default(false),
});
export type TransitionGuard = z.infer<typeof transitionGuardSchema>;

export const transitionGuardListSchema = z.array(transitionGuardSchema);

// --- Targets (decision 2) ------------------------------------------------

/**
 * Where a step leads. Modelled as a discriminated union rather than a nullable
 * status id, so "any status" is a first-class answer instead of a missing value
 * every reader has to interpret.
 */
export const transitionTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('status'), statusId: z.uuid() }),
  z.object({ kind: z.literal('stage'), salesStage: z.number() }),
  z.object({ kind: z.literal('any') }),
]);
export type TransitionTarget = z.infer<typeof transitionTargetSchema>;

/** Stable key for a target, used to de-duplicate the steps out of one status. */
export const transitionTargetKey = (target: TransitionTarget): string => {
  if (target.kind === 'status') return `status:${target.statusId}`;
  if (target.kind === 'stage') return `stage:${target.salesStage}`;
  return 'any';
};

/**
 * Decision 4, in one place so the editor, the API and any future enforcement
 * agree: a status outside the process may only lead back to Stage 1.
 */
export const isTargetAllowedFromStage = (
  fromSalesStage: number,
  target: TransitionTarget,
  salesStageOfStatus: (statusId: string) => number | undefined
): boolean => {
  if (fromSalesStage !== UNPLACED_SALES_STAGE) return true;
  if (target.kind === 'any') return false;
  if (target.kind === 'stage') return target.salesStage === REENTRY_SALES_STAGE;
  return salesStageOfStatus(target.statusId) === REENTRY_SALES_STAGE;
};

/**
 * The stages the "advance to next status" button walks, in order.
 *
 * Cancelled (20) is deliberately absent, and so is Not-in-the-process (0). The
 * button is a shortcut along the happy path; cancelling is a decision with
 * consequences (it stamps the cancellation date and sweeps every item to S91)
 * and stays a deliberate dropdown choice with its reason modal behind it. Same
 * shape as the Operations card's checkmark, which excludes its own
 * exception/terminal codes from the advance chain for the same reason.
 */
export const ADVANCING_SALES_STAGES: readonly number[] = [1, 4, 10];

/** Stage 3 (XT-816): agent-portal request awaiting the consultant's price check. */
export const PRICE_REVIEW_SALES_STAGE = 3;
const PROPOSAL_SALES_STAGE = 4;

/**
 * The status the button should move an itinerary to: the nominated default of
 * the NEXT advancing stage after the current status's stage.
 *
 * Returns null — meaning "offer no button" — when there is nowhere sensible to
 * go: the current status is outside the advancing chain (unplaced or
 * cancelled), it is already at the last stage, or the next stage has no default
 * nominated. That last case is why the button is absent rather than guessing:
 * with several statuses in a stage, picking one for the user would be inventing
 * the process rather than following it.
 *
 * A stage holding exactly ONE status needs no nomination — there is nothing to
 * disambiguate, so that status is its own default. This keeps a simple linear
 * process working with no configuration at all.
 */
/**
 * Price Review sits off the happy path (only agent-portal requests enter it),
 * so Draft still advances straight to Proposal; out of it, Proposal.
 */
const nextAdvancingStage = (salesStage: number): number | null => {
  if (salesStage === PRICE_REVIEW_SALES_STAGE) return PROPOSAL_SALES_STAGE;
  const index = ADVANCING_SALES_STAGES.indexOf(salesStage);
  if (index < 0) return null;
  return ADVANCING_SALES_STAGES[index + 1] ?? null;
};

export const resolveNextStatus = <
  T extends {
    id: string;
    salesStage: number | null;
    isStageDefault: boolean;
  },
>(
  statuses: readonly T[],
  currentStatusId: string | null
): T | null => {
  if (!currentStatusId) return null;
  const current = statuses.find((s) => s.id === currentStatusId);
  if (!current || current.salesStage == null) return null;

  const nextStage = nextAdvancingStage(current.salesStage);
  if (nextStage == null) return null;

  const inNextStage = statuses.filter((s) => s.salesStage === nextStage);
  if (inNextStage.length === 0) return null;
  if (inNextStage.length === 1) return inNextStage[0] ?? null;
  return inNextStage.find((s) => s.isStageDefault) ?? null;
};

/**
 * A step may never lead INTO a status that is out of the process.
 *
 * Being out of the process means nothing steps into it: decision 4 lets an
 * unplaced status lead only back to Stage 1, so a step INTO one would be an
 * exit that no configured step could reverse. Kept beside decision 4 rather
 * than folded into it so the editor and the API can report the two separately —
 * they fail for different reasons and deserve different messages.
 *
 * Only an explicit `status` target is checked. The `any` and `stage` wildcards
 * are unaffected: they describe a breadth ("wherever the process allows"), and
 * narrowing what they resolve to is enforcement's job, not the editor's.
 */
export const isTargetInProcess = (
  target: TransitionTarget,
  salesStageOfStatus: (statusId: string) => number | undefined
): boolean =>
  target.kind !== 'status' ||
  salesStageOfStatus(target.statusId) !== UNPLACED_SALES_STAGE;

// --- The machine ---------------------------------------------------------

export const statusMachineStateSchema = z.object({
  id: z.uuid(),
  status: z.string().nullable(),
  tcStatus: z.string().nullable(),
  color: z.string().nullable(),
  /** Decision 1: the stage IS the position in the process. */
  salesStage: z.number().nullable(),
  isOperational: z.boolean(),
  /** The status this stage advances to (XT-708). At most one per stage. */
  isStageDefault: z.boolean(),
  /**
   * The step this status plays in the agent approval process — where arrows
   * 0, D, L and F land. Null keeps the detection by name.
   */
  processStep: itineraryProcessStepSchema.nullable().optional(),
  processSteps: itineraryProcessTargetsSchema.optional(),
  /** Read-only: what the application already does. */
  onEnter: statusActionListSchema,
  onExit: statusActionListSchema,
  /**
   * XT-798 — the actions ATTACHED to this status, which is what the board
   * renders. Distinct from `onEnter` above: that describes what the code does
   * for an organization that has not been migrated, and cannot be changed.
   */
  actions: z
    .array(
      z.object({
        bindingId: z.uuid(),
        actionId: z.uuid(),
        name: z.string(),
        type: z.string(),
        phase: z.string(),
        description: z.string().nullable(),
        isEnabled: z.boolean(),
      })
    )
    .default([]),
  /** Itineraries currently sitting in this state — context for the screen. */
  itineraryCount: z.number().int().nonnegative(),
});
export type StatusMachineState = z.infer<typeof statusMachineStateSchema>;

export const statusMachineTransitionSchema = z.object({
  id: z.uuid(),
  fromStatusId: z.uuid(),
  target: transitionTargetSchema,
  guards: transitionGuardListSchema,
  /**
   * Actions bound to this EDGE rather than to either status. Some real triggers
   * key on the pair — `task_collection.status_config` fires only for a specific
   * from → to, which is what the Task Collections form configures — so putting
   * them on the destination state would claim they fire however it was reached.
   */
  onTransition: statusActionListSchema,
});
export type StatusMachineTransition = z.infer<
  typeof statusMachineTransitionSchema
>;

export const statusMachineOverviewSchema = z.object({
  states: z.array(statusMachineStateSchema),
  transitions: z.array(statusMachineTransitionSchema),
});
export type StatusMachineOverview = z.infer<typeof statusMachineOverviewSchema>;

/** Steps out of a status, for the editor and any future "next step" UI. */
export const transitionsFrom = (
  transitions: readonly StatusMachineTransition[],
  fromStatusId: string
): StatusMachineTransition[] =>
  transitions.filter((t) => t.fromStatusId === fromStatusId);

// --- Writes --------------------------------------------------------------

/**
 * The editor saves the whole graph at once. A diff-based API would have to
 * reconcile concurrent edits of a small, wholly-visible config; replacing it
 * inside one transaction is simpler and matches how the screen is used.
 */
export const saveStatusMachineInput = z.object({
  /**
   * Stage assignments — which column each status sits in, and which of them is
   * the one its stage advances to. Sent for every status the editor shows, so
   * an omitted `isStageDefault` is a real "not the default", not a no-op.
   */
  stages: z.array(
    z.object({
      statusId: z.uuid(),
      salesStage: z.number().int(),
      isStageDefault: z.boolean().default(false),
      /** Omitted leaves the stored step as it is; null clears it. */
      processStep: itineraryProcessStepSchema.nullable().optional(),
      processSteps: itineraryProcessTargetsSchema.optional(),
    })
  ),
  transitions: z.array(
    z.object({
      fromStatusId: z.uuid(),
      target: transitionTargetSchema,
      guards: transitionGuardListSchema.default([]),
      onTransition: statusActionListSchema.default([]),
    })
  ),
});
export type SaveStatusMachineInput = z.infer<typeof saveStatusMachineInput>;
